import type { ComponentType } from "react";
import { memo } from "react";
import {
  Brain,
  Compass,
  FileText,
  Lightbulb,
  Search,
  Sparkles,
  Target,
  Telescope,
} from "lucide-react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useSuggestedPromptsVisibility } from "@/hooks/useSuggestedPromptsVisibility";
import type { ChatGreeting, PersonalizedSuggestion } from "@/services/chatSuggestionsService";

interface ChatEmptyStateProps {
  chatGreeting: ChatGreeting;
  personalizedSuggestions: PersonalizedSuggestion[];
  suggestionsLoading: boolean;
  onSuggestionClick: (prompt: string) => void;
  minimal?: boolean;
}

const iconMap: Record<string, ComponentType<{ className?: string }>> = {
  Sparkles,
  FileText,
  Target,
  Search,
  Brain,
  Lightbulb,
  Compass,
  Telescope,
};

export const ChatEmptyState = memo(function ChatEmptyState({
  chatGreeting,
  personalizedSuggestions,
  suggestionsLoading,
  onSuggestionClick,
  minimal = false,
}: ChatEmptyStateProps) {
  // DS EDIT 8: account-synced (survives refresh, sign-out/in, other devices)
  const { value: showSuggestedPrompts, setValue: setShowSuggestedPrompts } =
    useSuggestedPromptsVisibility(true);

  if (minimal) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-4">
        <div className="p-3 bg-primary/5 rounded-full mb-3">
          <Sparkles className="h-8 w-8 text-primary" />
        </div>
        <p className="text-sm text-muted-foreground" data-testid="chat-greeting-minimal">
          Ask anything to get started
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center h-full text-center">
      <div className="p-4 bg-primary/5 rounded-full mb-4">
        <Sparkles className="h-12 w-12 text-primary" />
      </div>
      <h2 className="text-xl font-semibold mb-2" data-testid="chat-greeting-headline">
        {chatGreeting.headline}
      </h2>
      <p className="text-muted-foreground max-w-md mb-6" data-testid="chat-greeting-subtext">
        {chatGreeting.subtext}
      </p>
      <div className="flex items-center justify-center gap-2 max-w-lg w-full px-4 mb-3">
        <Label htmlFor="toggle-suggested-prompts" className="text-muted-foreground cursor-pointer">
          Suggested prompts
        </Label>
        <Switch
          id="toggle-suggested-prompts"
          checked={showSuggestedPrompts}
          onCheckedChange={setShowSuggestedPrompts}
          data-testid="toggle-suggested-prompts"
        />
      </div>
      {showSuggestedPrompts ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-lg w-full px-4">
          {suggestionsLoading ? (
            <>
              {[0, 1, 2, 3].map((idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-2 p-3 rounded-lg border bg-card animate-pulse"
                >
                  <div className="h-5 w-5 bg-muted rounded shrink-0" />
                  <div className="flex-1 space-y-1">
                    <div className="h-3 bg-muted rounded w-3/4" />
                    <div className="h-3 bg-muted rounded w-1/2" />
                  </div>
                </div>
              ))}
            </>
          ) : (
            personalizedSuggestions.map((item, idx) => {
              const IconComponent = iconMap[item.icon] || Sparkles;
              return (
                <button
                  key={idx}
                  onClick={() => onSuggestionClick(item.prompt)}
                  className="flex items-center gap-2 p-3 text-left text-sm rounded-lg border bg-card hover-elevate transition-colors"
                  data-testid={`prompt-suggestion-${idx}`}
                >
                  <IconComponent className="h-5 w-5 text-primary shrink-0" />
                  <span className="text-muted-foreground line-clamp-2">{item.prompt}</span>
                </button>
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
});
